import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { ScrollArea } from "../ui/scroll-area";
import { Button } from "../ui/button";
import {
  ChevronRight,
  Database,
  KeyRound,
  Search,
  Copy,
  RefreshCw,
} from "lucide-react";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { toast } from "sonner";

export default function SchemaExplorerSheet({ open, onOpenChange, datasource }) {
  const t = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");

  async function load() {
    if (!datasource?.id) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/datasources/${datasource.id}/schema`);
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, datasource?.id]);

  const tables = (data?.tables || []).filter((tbl) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    if (tbl.name.toLowerCase().includes(q)) return true;
    return tbl.columns.some((c) => c.name.toLowerCase().includes(q));
  });

  function copy(text) {
    navigator.clipboard?.writeText(text);
    toast.success(t("common.copied"));
  }

  function buildStarter(tbl) {
    const ycol = tbl.columns.find((c) => /int|float|num|real|double|decimal/i.test(c.type))?.name || "value";
    const xcol =
      tbl.columns.find((c) => /date|time|day/i.test(c.name) || /date|time|timestamp/i.test(c.type))?.name ||
      tbl.columns[0]?.name ||
      "id";
    return `SELECT ${xcol} AS x, ${ycol} AS y\nFROM ${tbl.name}\nORDER BY ${xcol}`;
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md" data-testid="schema-explorer-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Database className="h-4 w-4 text-primary" />
            {t("schema.title")}
          </SheetTitle>
          <SheetDescription>
            {datasource ? (
              <>
                <span className="font-medium">{datasource.name}</span> ·{" "}
                <span className="font-mono uppercase text-[10px]">{datasource.kind}</span>
                {data?.schema ? <> · <code className="text-[11px]">{data.schema}</code></> : null}
              </>
            ) : null}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={t("schema.filter")}
              className="pl-8 h-9 text-sm"
              data-testid="schema-filter"
            />
          </div>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={load} data-testid="schema-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>

        <div className="mt-4">
          {loading ? (
            <div className="text-sm text-muted-foreground">{t("schema.loading")}</div>
          ) : error ? (
            <div className="text-sm text-destructive border border-destructive/30 bg-destructive/10 rounded-md p-3">
              {error}
            </div>
          ) : tables.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              {data ? t("schema.no_match") : t("schema.no_data")}
            </div>
          ) : (
            <ScrollArea className="h-[calc(100vh-14rem)] pr-3">
              <div className="space-y-1.5">
                {tables.map((tbl) => (
                  <Collapsible key={tbl.name} className="card-soft" data-testid={`schema-table-${tbl.name}`}>
                    <CollapsibleTrigger asChild>
                      <button className="w-full text-left p-3 flex items-center justify-between gap-2 hover:bg-accent/40 transition-colors group">
                        <div className="flex items-center gap-2 min-w-0">
                          <ChevronRight className="h-3.5 w-3.5 transition-transform [&[data-state=open]]:rotate-90 group-data-[state=open]:rotate-90" />
                          <span className="font-mono text-sm font-medium truncate">{tbl.name}</span>
                        </div>
                        <Badge variant="outline" className="text-[10px]">
                          {t("schema.cols", { n: tbl.columns ? tbl.columns.length : 0 })}
                        </Badge>
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="border-t border-border">
                      <div className="px-3 py-2 space-y-1">
                        {tbl.columns.map((c) => (
                          <div
                            key={c.name}
                            className="flex items-center justify-between gap-2 py-1 text-xs"
                          >
                            <div className="flex items-center gap-1.5 min-w-0">
                              {c.primary_key ? (
                                <KeyRound className="h-3 w-3 text-amber-500 shrink-0" />
                              ) : (
                                <span className="h-3 w-3 shrink-0" />
                              )}
                              <span className="font-mono truncate">{c.name}</span>
                            </div>
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono shrink-0">
                              {c.type}
                            </span>
                          </div>
                        ))}
                        <div className="flex justify-between items-center pt-2 mt-2 border-t border-border">
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                            {t("schema.starter")}
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-[11px]"
                            onClick={() => copy(buildStarter(tbl))}
                            data-testid={`copy-sql-${tbl.name}`}
                          >
                            <Copy className="h-3 w-3 mr-1" /> {t("common.copy")}
                          </Button>
                        </div>
                        <pre className="text-[11px] font-mono bg-muted/40 rounded-md p-2 whitespace-pre-wrap">
                          {buildStarter(tbl)}
                        </pre>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
